/**
 * Budget Service
 *
 * API client for budget-related endpoints.
 */

import { fetchApi } from '@/shared/services/api';
import type {
  BudgetAnalytics,
  BudgetRead,
  BudgetCreate,
  BudgetUpdate,
  BudgetSuggestion,
  BudgetWithProgress,
  PaginatedResponse,
} from '@/shared/types';

export interface BudgetListParams {
  category_id?: string;
  user_id?: string;
  period_type?: 'weekly' | 'monthly' | 'yearly';
  active_only?: boolean;
  page?: number;
  page_size?: number;
}

export interface BudgetProgressParams {
  period_start?: string;
  period_end?: string;
}

export const budgetService = {
  /**
   * List all budgets
   */
  list: (params?: BudgetListParams) =>
    fetchApi<PaginatedResponse<BudgetRead>>('/budgets', {
      params: params as Record<string, unknown>,
    }),

  /**
   * List budgets with spending progress
   */
  listWithProgress: (params?: BudgetProgressParams) =>
    fetchApi<BudgetWithProgress[]>('/budgets/progress', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get a single budget by ID
   */
  getById: (id: string) =>
    fetchApi<BudgetRead>(`/budgets/${id}`),

  /**
   * Get budget progress for a specific budget
   */
  getProgress: (id: string, params?: BudgetProgressParams) =>
    fetchApi<BudgetWithProgress>(`/budgets/${id}`, {
      params: params as Record<string, unknown>,
    }),

  /**
   * Create a new budget
   */
  create: (data: BudgetCreate) =>
    fetchApi<BudgetRead>('/budgets', { method: 'POST', body: data }),

  /**
   * Update a budget
   */
  update: (id: string, data: BudgetUpdate) =>
    fetchApi<BudgetRead>(`/budgets/${id}`, { method: 'PATCH', body: data }),

  /**
   * Delete a budget
   */
  delete: (id: string) =>
    fetchApi<void>(`/budgets/${id}`, { method: 'DELETE' }),

  /**
   * Get budget suggestions (unbudgeted categories with high spending)
   */
  suggestions: () =>
    fetchApi<BudgetSuggestion[]>('/budgets/suggestions'),

  /**
   * Get analytics data for a specific budget
   */
  getAnalytics: (id: string) =>
    fetchApi<BudgetAnalytics>(`/budgets/${id}/analytics`),
};
